// WorkMap.js v3.0 - Connected to Real Backend + Live Google Map
// GPS tracking, real project data, crew location logging
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MapPin, Play, Square, Clock, ArrowLeft, Navigation, Loader, RefreshCw, AlertTriangle
} from 'lucide-react';
import { loadProjects, loadSegments, readSheet, appendRow } from '../services/mapService';
import { isGpsEnabled } from '../services/gpsService';
import { GOOGLE_MAPS_API_KEY } from '../config/mapConfig';
import { getTheme } from '../utils/theme';

const WORK_LOG_SS = '1mhO4eZ-07SWM2VOjHcZML7vne9dMyT33O0bSI1DzcC8';

function WorkMap({ darkMode, user, userType, setCurrentPage, loggedInUser }) {
  const currentUser = user || loggedInUser;
  const theme = getTheme(darkMode);
  const { accentPrimary, accentSecondary, bgAlt: bgColor, cardBg, text: textColor, textMedium: mutedColor, border: borderColor } = theme.colors;

  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [trackingStart, setTrackingStart] = useState(null);
  const [message, setMessage] = useState(null);
  const [showVersion, setShowVersion] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylinesRef = useRef([]);
  const markerRef = useRef(null);

  // Load projects on mount
  useEffect(() => {
    async function init() {
      try {
        const projs = await loadProjects();
        const valid = projs.filter(p => p && p.project_id && p.project_id !== 'OFFLINE');
        setProjects(valid);
        if (valid.length > 0) {
          setActiveProject(valid[0]);
        }
      } catch (err) {
        console.error('[WorkMap] Failed to load projects:', err);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  // Load segments when project changes
  useEffect(() => {
    if (!activeProject) return;
    async function loadSegs() {
      try {
        const segs = await loadSegments(activeProject.project_id);
        setSegments(segs);
      } catch (err) {
        console.error('[WorkMap] Failed to load segments:', err);
        setSegments([]);
      }
    }
    loadSegs();
  }, [activeProject]);

  // Initialize Google Map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    function initMap() {
      if (!mapRef.current || mapInstanceRef.current) return;
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 30.1, lng: -93.2 },
        zoom: 14,
        mapTypeId: (() => { try { const v = localStorage.getItem('lyt-map-type'); return v === 'satellite' ? 'hybrid' : (v || 'hybrid'); } catch { return 'hybrid'; } })(),
        gestureHandling: 'greedy',
        styles: darkMode ? [{ elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] }] : [],
      });
      setMapReady(true);
    }

    if (window.google?.maps) {
      initMap();
    } else {
      // Check if script is already being loaded by another component
      const existing = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existing) {
        // Wait for existing script to load
        const check = setInterval(() => {
          if (window.google?.maps) { clearInterval(check); initMap(); }
        }, 100);
        return () => clearInterval(check);
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.onload = () => initMap();
      document.head.appendChild(script);
    }
  }, [darkMode]);

  // Draw segments on map
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    // Clear old polylines
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];

    if (!segments.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidCoords = false;

    segments.forEach(seg => {
      const startLat = parseFloat(seg.gps_start_lat || seg.gps_start?.lat);
      const startLng = parseFloat(seg.gps_start_lng || seg.gps_start?.lng);
      const endLat = parseFloat(seg.gps_end_lat || seg.gps_end?.lat);
      const endLng = parseFloat(seg.gps_end_lng || seg.gps_end?.lng);

      if (!startLat || !startLng || !endLat || !endLng) return;
      if (Math.abs(startLat) < 0.001 && Math.abs(startLng) < 0.001) return;

      hasValidCoords = true;
      const path = [{ lat: startLat, lng: startLng }, { lat: endLat, lng: endLng }];
      bounds.extend(path[0]);
      bounds.extend(path[1]);

      // Color by boring status
      const status = seg.boring_status || 'Not Started';
      const color = status === 'QC Approved' ? '#2196F3'
        : status === 'Complete' ? '#4CAF50'
        : status === 'In Progress' ? '#FFB800'
        : '#FF4444';

      const poly = new window.google.maps.Polyline({
        path,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 4,
        map,
      });
      polylinesRef.current.push(poly);
    });

    if (hasValidCoords) {
      map.fitBounds(bounds, 50);
    }
  }, [segments, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // GPS tracking (respects settings toggle)
  useEffect(() => {
    if (tracking && isGpsEnabled()) {
      const watchId = navigator.geolocation?.watchPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCurrentLocation(loc);
          // Update marker on map
          const map = mapInstanceRef.current;
          if (map) {
            if (!markerRef.current) {
              markerRef.current = new window.google.maps.Marker({
                position: loc,
                map,
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#4CAF50',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 3,
                },
                title: 'Your Location',
              });
            } else {
              markerRef.current.setPosition(loc);
            }
          }
        },
        (err) => console.error('GPS error:', err),
        { enableHighAccuracy: true }
      );
      return () => {
        navigator.geolocation?.clearWatch(watchId);
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
      };
    }
  }, [tracking]);

  const showMsg = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const startTracking = async () => {
    if (!activeProject) {
      showMsg('error', 'Please select a project first');
      return;
    }
    setTracking(true);
    setTrackingStart(new Date());
    try {
      await appendRow(WORK_LOG_SS, 'WorkSessions!A:G', [[
        new Date().toISOString(),
        currentUser?.email || '',
        currentUser?.name || '',
        activeProject.project_id,
        'STARTED',
        currentLocation ? `${currentLocation.lat},${currentLocation.lng}` : '',
        '',
      ]]);
    } catch (err) {
      console.error('[WorkMap] Failed to log start:', err);
    }
    showMsg('success', 'Tracking started');
  };

  const stopTracking = async () => {
    setTracking(false);
    const duration = trackingStart ? Math.round((Date.now() - trackingStart.getTime()) / 60000) : 0;
    try {
      await appendRow(WORK_LOG_SS, 'WorkSessions!A:G', [[
        new Date().toISOString(),
        currentUser?.email || '',
        currentUser?.name || '',
        activeProject?.project_id || '',
        'STOPPED',
        currentLocation ? `${currentLocation.lat},${currentLocation.lng}` : '',
        `${duration} min`,
      ]]);
    } catch (err) {
      console.error('[WorkMap] Failed to log stop:', err);
    }
    setTrackingStart(null);
    showMsg('success', `Session saved (${duration} min)`);
  };

  const goBack = () => {
    const role = currentUser?.role || userType || 'employee';
    if (role === 'admin' || role === 'Admin') setCurrentPage('admin-dashboard');
    else if (role === 'contractor' || role === 'Contractor') setCurrentPage('contractor-dashboard');
    else setCurrentPage('employee-dashboard');
  };

  const elapsed = tracking && trackingStart
    ? Math.round((Date.now() - trackingStart.getTime()) / 60000)
    : 0;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <Loader size={32} color={accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ color: mutedColor }}>Loading work map...</span>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px' }} onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}>
      {message && (
        <div style={{ position: 'fixed', top: 20, right: 20, padding: '14px 22px', backgroundColor: message.type === 'success' ? accentSecondary : '#ef4444', color: '#fff', borderRadius: 8, zIndex: 1000, fontWeight: 500, fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <button onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 8, color: textColor, cursor: 'pointer', fontSize: '0.85rem' }}>
            <ArrowLeft size={18} /> Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: textColor, margin: 0 }}>Work Map</h1>
            <p style={{ color: mutedColor, margin: 0, fontSize: '0.85rem' }}>Field view</p>
          </div>
        </div>

        {/* GPS tracking controls removed — GPS controlled from Settings */}

        {/* Project Selector */}
        <div style={{ backgroundColor: cardBg, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: textColor, margin: 0 }}>Project</h3>
            <span style={{ fontSize: '0.75rem', color: mutedColor }}>{projects.length} available</span>
          </div>
          {projects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: mutedColor }}>
              <AlertTriangle size={24} style={{ marginBottom: 8 }} />
              <div>No projects found</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {projects.map(project => {
                const isActive = activeProject?.project_id === project.project_id;
                return (
                  <button
                    key={project.project_id}
                    onClick={() => setActiveProject(project)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: 14, backgroundColor: isActive ? `${accentPrimary}15` : (darkMode ? '#1a2633' : '#f8fafc'),
                      border: isActive ? `2px solid ${accentPrimary}` : `1px solid ${borderColor}`,
                      borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500, color: textColor, fontSize: '0.95rem' }}>{project.project_id}</div>
                      <div style={{ fontSize: '0.8rem', color: mutedColor }}>{project.name || project.client || ''}</div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: isActive ? accentPrimary : mutedColor, fontWeight: isActive ? 600 : 400 }}>
                      {project.status || 'Active'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Google Map */}
        <div style={{ backgroundColor: cardBg, borderRadius: 12, overflow: 'hidden', border: `1px solid ${borderColor}`, marginBottom: 20 }}>
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin size={16} color={accentPrimary} />
              <span style={{ fontWeight: 500, color: textColor, fontSize: '0.9rem' }}>
                {activeProject ? `${activeProject.project_id} — ${segments.length} segments` : 'Select a project'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: '0.7rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 3, backgroundColor: '#FF4444', display: 'inline-block', borderRadius: 1 }} /> Not Started</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 3, backgroundColor: '#FFB800', display: 'inline-block', borderRadius: 1 }} /> In Progress</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 3, backgroundColor: '#4CAF50', display: 'inline-block', borderRadius: 1 }} /> Complete</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 3, backgroundColor: '#2196F3', display: 'inline-block', borderRadius: 1 }} /> QC Approved</span>
            </div>
          </div>
          <div ref={mapRef} style={{ width: '100%', height: 450 }} />
        </div>

        {/* Segment Summary */}
        {segments.length > 0 && (
          <div style={{ backgroundColor: cardBg, borderRadius: 12, padding: 20, border: `1px solid ${borderColor}` }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: textColor, margin: '0 0 12px' }}>Segment Status</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
              {[
                { label: 'Total', value: segments.length, color: textColor },
                { label: 'Not Started', value: segments.filter(s => s.boring_status === 'Not Started').length, color: '#FF4444' },
                { label: 'In Progress', value: segments.filter(s => s.boring_status === 'In Progress').length, color: '#FFB800' },
                { label: 'Complete', value: segments.filter(s => s.boring_status === 'Complete').length, color: '#4CAF50' },
                { label: 'QC Approved', value: segments.filter(s => s.boring_status === 'QC Approved').length, color: '#2196F3' },
              ].map(stat => (
                <div key={stat.label} style={{ textAlign: 'center', padding: 12, backgroundColor: darkMode ? '#0d1b2a' : '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '0.75rem', color: mutedColor }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showVersion && (
        <div style={{ position: 'fixed', bottom: 10, right: 10, fontSize: '0.7rem', opacity: 0.5, color: textColor, backgroundColor: cardBg, padding: '4px 8px', borderRadius: 4 }}>
          WorkMap v3.0
        </div>
      )}
    </div>
  );
}

export default WorkMap;
